/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { AppCommand } from "~/types/commands";
import { ActionDefinition } from "~/types/actions";

/**
 * Helper function to check if an element is an input-like element where keyboard input should not trigger shortcuts
 */
const isInputElement = (element: HTMLElement): boolean => {
  const tag = element.tagName;
  if (tag === "INPUT") {
    const type = element.getAttribute("type");
    // Allow button inputs to trigger shortcuts
    return type !== "button" && type !== "checkbox" && type !== "radio";
  }
  if (tag === "TEXTAREA") return true;
  if (element.isContentEditable) return true;
  if (element.closest("dialog")) return true;
  return false;
};

export type FocusMeta = Record<string, unknown> & {
  role?: string;
  name?: string;
  characterId?: string;
};

export interface FocusContextItem {
  id: string;
  type: string;
  meta: FocusMeta;
  actions?: ActionDefinition[];
  onCommand?: (command: AppCommand) => void;
  parentId?: string;
}

interface FocusContextType {
  focusStack: FocusContextItem[];
  registerFocus: (id: string, parentId: string | undefined, type: string, meta: FocusMeta, onCommand?: (command: AppCommand) => void, actions?: ActionDefinition[]) => void;
  unregisterFocus: (id: string) => void;
  updateFocus: (id: string, meta: FocusMeta, onCommand?: (command: AppCommand) => void, actions?: ActionDefinition[]) => void;
  activate: (id: string) => void;
  dispatchCommand: (command: AppCommand, targetId?: string) => void;
  isOverlayOpen: boolean;
  setIsOverlayOpen: (isOpen: boolean) => void;
}

const FocusContext = createContext<FocusContextType | null>(null);
const FocusParentContext = createContext<string | undefined>(undefined);

export const useFocusContext = () => {
  const context = useContext(FocusContext);
  if (!context) {
    throw new Error("useFocusContext must be used within a FocusProvider");
  }
  return context;
};

export const FocusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [focusStack, setFocusStack] = useState<FocusContextItem[]>([]);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const registerFocus = useCallback((id: string, parentId: string | undefined, type: string, meta: FocusMeta, onCommand?: (command: AppCommand) => void, actions?: ActionDefinition[]) => {
    setFocusStack((prev) => [...prev, { id, parentId, type, meta, onCommand, actions }]);
  }, []);

  const unregisterFocus = useCallback((id: string) => {
    setFocusStack((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const updateFocus = useCallback((id: string, meta: FocusMeta, onCommand?: (command: AppCommand) => void, actions?: ActionDefinition[]) => {
    setFocusStack((prev) =>
      prev.map((item) => (item.id === id ? { ...item, meta, onCommand, actions } : item))
    );
  }, []);

  const activate = useCallback((id: string) => {
    setFocusStack((prev) => {
      const itemIndex = prev.findIndex((item) => item.id === id);
      if (itemIndex === -1 || itemIndex === prev.length - 1) return prev;

      const item = prev[itemIndex];
      const newStack = [...prev];
      newStack.splice(itemIndex, 1);
      newStack.push(item);
      return newStack;
    });
  }, []);

  // Use a ref to access the current stack inside dispatchCommand without making it a dependency
  const stackRef = useRef(focusStack);
  useEffect(() => {
    stackRef.current = focusStack;
  }, [focusStack]);

  const dispatchCommand = useCallback((command: AppCommand, targetId?: string) => {
    const currentStack = stackRef.current;

    if (targetId) {
      const target = currentStack.find((item) => item.id === targetId);
      target?.onCommand?.(command);
    } else {
      // Broadcast to all active listeners if no target specified
      [...currentStack].reverse().forEach((item) => {
        item.onCommand?.(command);
      });
    }
  }, []);

  /**
   * Global keybind listener
   * Listens for keyboard shortcuts defined in ActionDefinitions across the focus stack.
   * Traverses from the top (active context) downward, including container contexts.
   */
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement;

      // Skip if the event target is an input-like element
      if (isInputElement(target)) {
        return;
      }

      // Build key combination string with modifiers
      const modifiers: string[] = [];
      if (event.ctrlKey) modifiers.push('ctrl');
      if (event.metaKey) modifiers.push('meta');
      if (event.altKey) modifiers.push('alt');
      if (event.shiftKey) modifiers.push('shift');

      const baseKey = event.key.toLowerCase();
      const keyCombo = modifiers.length > 0
        ? `${modifiers.join('+')}+${baseKey}`
        : baseKey;

      // Skip if an overlay (modal, palette, etc.) is open
      if (isOverlayOpen) {
        return;
      }

      // Build hierarchy to check: Active -> Parent -> Parent ...
      const contextsToCheck: FocusContextItem[] = [];
      if (focusStack.length > 0) {
        // Start with the active item (last in stack)
        let currentItem: FocusContextItem | undefined = focusStack[focusStack.length - 1];
        while (currentItem) {
          contextsToCheck.push(currentItem);
          // Find parent
          const pid: string | undefined = currentItem.parentId;
          currentItem = pid ? focusStack.find(item => item.id === pid) : undefined;
        }
      }

      // Search for a matching shortcut in the contexts
      for (const context of contextsToCheck) {
        if (!context.actions) continue;

        const matchingAction = context.actions.find((action) => {
          if (!action.shortcut) return false;
          const shortcuts = Array.isArray(action.shortcut) ? action.shortcut : [action.shortcut];

          return shortcuts.some((s) => {
            // Normalize shortcut: lowercase, replace spaces with +, and standardize separators
            const shortcutKey = s.toLowerCase().replace(/\s+/g, '+');

            // Direct match
            if (shortcutKey === keyCombo) return true;

            // Cross-platform Meta key support:
            // If shortcut uses "meta", also match "ctrl" (for Windows/Linux)
            // If shortcut uses "ctrl", also match "meta" (for macOS users who might use CMD)
            if (shortcutKey.includes('meta')) {
              const ctrlVariant = shortcutKey.replace('meta', 'ctrl');
              if (ctrlVariant === keyCombo) return true;
            }
            if (shortcutKey.includes('ctrl')) {
              const metaVariant = shortcutKey.replace('ctrl', 'meta');
              if (metaVariant === keyCombo) return true;
            }

            return false;
          });
        });

        if (matchingAction) {
          // Found a matching shortcut
          event.preventDefault();

          // Create command from the action
          const command = {
            type: matchingAction.id,
            payload: {},
          } as AppCommand;

          // Dispatch to the specific context
          context.onCommand?.(command);

          // Stop searching - we found a match
          return;
        }
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [focusStack, isOverlayOpen]);

  const value = useMemo(
    () => ({ focusStack, registerFocus, unregisterFocus, updateFocus, activate, dispatchCommand, isOverlayOpen, setIsOverlayOpen }),
    [focusStack, registerFocus, unregisterFocus, updateFocus, activate, dispatchCommand, isOverlayOpen, setIsOverlayOpen]
  );

  return <FocusContext.Provider value={value}>{children}</FocusContext.Provider>;
};

export type ScopeActivateOn = 'click' | 'focus' | 'mousedown' | 'none';

export interface ScopeProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  children: React.ReactNode;
  /**
   * When to automatically activate this focus context.
   * - 'click': Activate on click
   * - 'focus': Activate when any descendant receives focus (default)
   * - 'mousedown': Activate on mouse down (useful for drag scenarios)
   * - 'none': No automatic activation
   */
  activateOn?: ScopeActivateOn;
  /**
   * HTML element type to render. Defaults to React.Fragment when activateOn is 'none',
   * otherwise defaults to 'div'.
   */
  as?: React.ElementType;
}

/**
 * Hook to register a component in the focus stack.
 * Automatically adds to stack on mount and removes on unmount.
 * 
 * @param type The type identifier for this context (e.g. 'CharacterSheet')
 * @param meta Metadata describing the context state
 * @param onCommand Optional callback to handle commands dispatched to this context
 * @param actions Optional array of action definitions for this context
 * 
 * @returns Object containing:
 *   - `id`: Unique identifier for this focus context
 *   - `activate`: Function to programmatically focus this context
 *   - `Scope`: Component to wrap children that establishes parent focus context
 * 
 * @example
 * Basic usage with no auto-activation:
 * ```tsx
 * const MyComponent = () => {
 *   const { Scope } = useFocus("MyComponent", {}, handleCommand, actions);
 *   return (
 *     <Scope>
 *       {children}
 *     </Scope>
 *   );
 * };
 * ```
 * 
 * @example
 * With auto-activation on click:
 * ```tsx
 * const MyCard = () => {
 *   const { Scope } = useFocus("MyCard", {}, handleCommand, actions);
 *   return (
 *     <Scope activateOn="click" className={styles.card}>
 *       {children}
 *     </Scope>
 *   );
 * };
 * ```
 * 
 * @example
 * Manual activation when needed:
 * ```tsx
 * const MyComponent = () => {
 *   const { activate, Scope } = useFocus("MyComponent", {}, handleCommand, actions);
 *   return (
 *     <Scope>
 *       <button onClick={() => activate()}>Focus Me</button>
 *     </Scope>
 *   );
 * };
 * ```
 */
export const useFocus = (
  type: string,
  meta: FocusMeta = {},
  onCommand?: (command: AppCommand) => void,
  actions?: ActionDefinition[]
) => {
  const { registerFocus, unregisterFocus, updateFocus, activate } = useFocusContext();
  const parentId = useContext(FocusParentContext);

  // Stable ID for this component instance
  const id = useMemo(() => Math.random().toString(36).substring(2, 15), []);

  // Use a ref for onCommand to avoid re-registering/updating when only the handler reference changes
  const onCommandRef = useRef(onCommand);
  useEffect(() => {
    onCommandRef.current = onCommand;
  }, [onCommand]);

  const stableOnCommand = useCallback((command: AppCommand) => {
    if (onCommandRef.current) {
      onCommandRef.current(command);
    }
  }, []);

  // We serialize meta and actions to check for deep equality in the effect dependency
  const metaString = JSON.stringify(meta);
  const actionsString = JSON.stringify(actions);

  useEffect(() => {
    registerFocus(id, parentId, type, meta, stableOnCommand, actions);
    return () => {
      unregisterFocus(id);
    };
  }, [id, parentId, type, metaString, actionsString, registerFocus, unregisterFocus, stableOnCommand]);

  useEffect(() => {
    updateFocus(id, meta, stableOnCommand, actions);
  }, [id, metaString, actionsString, updateFocus, stableOnCommand]);

  const activateCallback = useCallback(() => {
    // NOTE: Pop a debug log here if needed to trace activations
    activate(id);
  }, [activate, id]);

  const Scope = useMemo(
    () => ({ children, activateOn = 'mousedown', as, ...rest }: ScopeProps) => {
      const content = (
        <FocusParentContext.Provider value={id}>{children}</FocusParentContext.Provider>
      );

      // If no activation trigger, use Fragment (no wrapper element)
      if (activateOn === 'none') {
        const Component = as || React.Fragment;
        return <Component {...(as ? rest : {})}>{content}</Component>;
      }

      // Otherwise, wrap in an element with the appropriate event handler
      const Component = as || 'div';
      const props: React.HTMLAttributes<HTMLDivElement> = { ...rest };

      switch (activateOn) {
        case 'click':
          props.onClick = (e) => {
            e.stopPropagation();
            activateCallback();
            rest.onClick?.(e);
          };
          break;
        case 'focus':
          props.onFocus = (e) => {
            e.stopPropagation();
            activateCallback();
            rest.onFocus?.(e);
          };
          break;
        case 'mousedown':
          props.onMouseDownCapture = (e) => {
            activateCallback();
            rest.onMouseDownCapture?.(e);
          };
          break;
      }

      return <Component {...props}>{content}</Component>;
    },
    [id, activateCallback]
  );

  return {
    id,
    activate: activateCallback,
    Scope,
  };
};

/**
 * @deprecated Use the `Scope` component returned from `useFocus` instead.
 * 
 * This standalone component is deprecated in favor of the `Scope` component
 * returned by `useFocus`, which eliminates the need to pass the `id` prop manually
 * and supports automatic activation.
 * 
 * @example
 * ```tsx
 * // Old pattern (deprecated):
 * const { id } = useFocus(...);
 * return <FocusScope id={id}>{children}</FocusScope>;
 * 
 * // New pattern (preferred):
 * const { Scope } = useFocus(...);
 * return <Scope>{children}</Scope>;
 * 
 * // With auto-activation:
 * return <Scope activateOn="click">{children}</Scope>;
 * ```
 */
export const FocusScope: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <FocusParentContext.Provider value={id}>{children}</FocusParentContext.Provider>
);
